import {
  AUTH_FAILED_REASON,
  AuthFailReason,
} from '$shared/types/auth-failed-reasons';
import {
  Event,
  OpenModalEvent,
  CloseModalEvent,
  OpenPopupEvent,
  SentryError,
  createLogger,
  envs,
  eventFactory,
} from '@sentry/shared';
import {
  CredentialTokenType,
  OriginValidation,
  validateOriginWithCredential,
} from './validate-origin';

export type PostMessagePort = { postMessage: (arg: any) => void };

export const AppRedirectEvent = Event.create({
  name: 'redirect',
  side: 'proxy',
});

export const AppDeeplinkEvent = Event.create({
  name: 'deeplink',
  side: 'proxy',
});
export const OnErrorEvent = Event.create<SentryError>({
  name: 'on-error',
  side: 'proxy',
  data: SentryError.critical('Unknown error.'),
});

export type AuthFailedEventData = {
  reason: AuthFailReason;
  fallbackUrl?: string;
};

export const OnAuthFailedEvent = Event.create<AuthFailedEventData>({
  name: 'on-auth-failed',
  side: 'proxy',
  data: {
    reason: AUTH_FAILED_REASON.unknown,
  },
});

export const HelloEvent = Event.create({
  name: 'hello',
  side: 'proxy',
  data: {
    name: 'hello',
  },
});

export const OnLoadEvent = Event.create({
  name: 'on-load',
  side: 'proxy',
});

export const OnTokenEvent = Event.create({ name: 'on-token', side: 'proxy' });
export const OnAuthCodeEvent = Event.create({
  name: 'on-auth-code',
  side: 'proxy',
});

export const OnProviderClicked = Event.create({
  name: 'on-provider-clicked',
  side: 'proxy',
});

export function getAppEvents(
  port: MessagePort,
  originValidation?: OriginValidation
) {
  const emitToken = eventFactory<string>(port)(OnTokenEvent);
  const emitAuthCode = eventFactory<string>(port)(OnAuthCodeEvent);
  const sendAuthFailed = eventFactory<AuthFailedEventData>(port)(
    OnAuthFailedEvent
  );

  // SECURITY (VULN-36931): never release a credential to the host
  // (asserted_origin) until that origin is validated against the client's
  // registered redirect URIs. The check runs here, at emit time, because only
  // now does a token exist to satisfy /ims/validate_client/v2. Fails closed —
  // an unvalidated origin gets on-auth-failed, not the credential.
  const releaseCredential = async (
    tokenType: CredentialTokenType,
    credential: string,
    emit: (value: string) => void,
    fallbackUrl?: string
  ): Promise<void> => {
    // Non-wrapper callers (no asserted_origin) keep prior behavior; only the
    // wrapper path carries an attacker-suppliable origin to validate.
    if (!originValidation) {
      emit(credential);
      return;
    }

    // Fail closed on ANY error, and never reject. The trait handlers
    // (handlers.ts) invoke sendToken/sendAuthCode fire-and-forget, so a
    // rejection here would surface as an unhandled promise rejection AND skip
    // the intended on-auth-failed. Any failure -> withhold + on-auth-failed.
    try {
      const isValid = await validateOriginWithCredential(
        originValidation,
        tokenType,
        credential
      );

      if (isValid) {
        emit(credential);
      } else {
        // VALIDATION (SUSILT-453): distinct signal to confirm a withheld
        // credential carries a recovery URL. All four popup flows (social,
        // adobe-sign-in, adobe-create-account, passkey) now thread a fallbackUrl,
        // so post-release has_fallback should read ~100% true — a material `false`
        // flags a flow that still dead-ends (regression), the only benign source
        // being the dead federated-social branch. token_type keeps the
        // access_token/code split. asserted_origin/token are never logged here.
        createLogger(
          envs.getAppEnv,
          originValidation.client_id,
          originValidation.relay,
          'credential-withheld'
        )({
          info: 'origin gate withheld credential',
          token_type: tokenType,
          has_fallback: Boolean(fallbackUrl),
        });
        sendAuthFailed({ reason: AUTH_FAILED_REASON.originInvalid, fallbackUrl });
      }
    } catch (e) {
      // Unreachable in practice — validateOriginWithCredential never throws and
      // the emitters are plain postMessage — but if the release wiring itself
      // throws we still fail closed AND surface it. Distinct f.name so a wiring
      // misfire is separable in Splunk from v2/gate failures.
      createLogger(
        envs.getAppEnv,
        originValidation.client_id,
        originValidation.relay,
        'credential-release-error'
      )({
        error: {
          name: 'credential release wiring threw',
          message: (e as Error)?.message,
        },
      });
      sendAuthFailed({ reason: AUTH_FAILED_REASON.originInvalid, fallbackUrl });
    }
  };

  return {
    redirect: eventFactory<string>(port)(AppRedirectEvent),
    openPopup: eventFactory<string>(port)(OpenPopupEvent),
    openModal: eventFactory<{
      dimensions: { height: number; width: number };
      reason: string;
    }>(port)(OpenModalEvent),
    closeModal: eventFactory(port)(CloseModalEvent),
    sendProvider: eventFactory<string>(port)(OnProviderClicked),
    sendToken: (token: string, fallbackUrl?: string) =>
      releaseCredential('access_token', token, emitToken, fallbackUrl),
    sendAuthCode: (code: string, fallbackUrl?: string) =>
      releaseCredential('authorization_code', code, emitAuthCode, fallbackUrl),
    sendAuthFailed,
    sendError: eventFactory<SentryError>(port)(OnErrorEvent),
    sendLoad: eventFactory<Object>(port)(OnLoadEvent),
  };
}

export function getMobileEvents(messenger: PostMessagePort) {
  const port = {
    postMessage: (event: Event) => messenger.postMessage(event),
  } as MessagePort;
  return {
    redirect: eventFactory<Object>(port)(AppRedirectEvent),
    deeplink: eventFactory<Object>(port)(AppDeeplinkEvent),
    sendError: eventFactory<SentryError>(port)(OnErrorEvent),
    sendHello: eventFactory<Object>(port)(HelloEvent),
  };
}

export type AppEvents = ReturnType<typeof getAppEvents>;
export type MobileEvents = ReturnType<typeof getMobileEvents>;

export function getEvents(
  messenger: MessagePort | PostMessagePort,
  originValidation?: OriginValidation
): AppEvents | MobileEvents {
  if (messenger instanceof MessagePort) {
    return getAppEvents(messenger, originValidation);
  } else {
    return getMobileEvents(messenger);
  }
}

export async function eventsReducer(event: Event, actions: AppEvents) {
  switch (true) {
    default:
      return console.log(event);
  }
}
